import { Modal, Box, Flex, Text, Paper, ThemeIcon } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import type { CollaborationPaymentSuccessData } from "../hooks/useCollaborationEngagementPayment";

type CollaborationPaymentSuccessModalProps = {
  opened: boolean;
  onClose: () => void;
  paymentData: CollaborationPaymentSuccessData | null;
};

export default function CollaborationPaymentSuccessModal({
  opened,
  onClose,
  paymentData,
}: CollaborationPaymentSuccessModalProps) {
  if (!paymentData) return null;

  const bothPartiesPaid = paymentData.newStatus === "BOTH_PAID";
  const bothNdaFullySigned = !!paymentData.bothNdaBecameSigned;
  const creditsCharged =
    typeof paymentData.creditsCharged === "number" ? paymentData.creditsCharged : null;
  const amountLabel =
    creditsCharged === null
      ? "N/A"
      : paymentData.paymentMethod === "Collaboration Credits"
        ? `${creditsCharged} Credits`
        : `$${creditsCharged}`;
  const creditsPendingBothParties =
    !bothPartiesPaid && creditsCharged !== null && creditsCharged === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} fz="lg">
          Payment Successful
        </Text>
      }
      centered
      radius="md"
      size="lg"
    >
      <Paper
        radius="lg"
        withBorder
        p="lg"
        mb="md"
        style={{
          background: "linear-gradient(to bottom, var(--mantine-color-green-0), white)",
        }}
      >
        <Flex direction="column" align="center" gap="sm">
          <ThemeIcon size={56} radius={56} color="green" variant="light">
            <IconCircleCheck size={32} />
          </ThemeIcon>
          <Text fz="lg" fw={500} ta="center">
            Payment Successful!
          </Text>
          <Text c="slate.5" fz="sm" ta="center">
            {bothPartiesPaid
              ? "Your collaboration workspace is now active"
              : creditsPendingBothParties
                ? "You've completed your step. Credits are debited from both companies only after the other party pays too."
                : "Your payment is complete — waiting for the other party to pay"}
          </Text>
        </Flex>
      </Paper>

      <Paper radius="lg" withBorder p="md" mb="md">
        <Text fw={500} mb="sm">
          Payment Details
        </Text>

        <Flex direction="column" gap="sm">
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              Transaction ID
            </Text>
            <Text fz="sm" fw={500}>
              {paymentData.transactionId}
            </Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              {creditsPendingBothParties ? "Credits charged" : "Amount Paid"}
            </Text>
            <Text fz="sm" fw={500}>
              {creditsPendingBothParties ? "After both parties pay" : amountLabel}
            </Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              Date
            </Text>
            <Text fz="sm" fw={500}>
              {paymentData.date}
            </Text>
          </Flex>
        </Flex>
      </Paper>

      <Paper
        radius="lg"
        p="md"
        mb="md"
        style={{
          background: "#2563EB0D",
          border: "1px solid #2563EB33",
        }}
      >
        <Text fw={500} mb="sm">
          What's Next?
        </Text>

        <Flex direction="column" gap="md">
          <Flex gap="md" align="flex-start">
            <ThemeIcon size={28} radius={28} color="primary">
              <Text fz="xs" fw={500} c="white">
                1
              </Text>
            </ThemeIcon>
            <Box>
              <Text fz="sm" fw={500}>
                Access Your Workspace
              </Text>
              <Text fz="xs" c="slate.5">
                {bothPartiesPaid
                  ? "View full company details, documents, and the NDA from your collaboration workspace"
                  : "Once both parties pay, your workspace and NDA will be available"}
              </Text>
            </Box>
          </Flex>

          <Flex gap="md" align="flex-start">
            <ThemeIcon size={28} radius={28} color="primary">
              <Text fz="xs" fw={500} c="white">
                2
              </Text>
            </ThemeIcon>
            <Box>
              <Text fz="sm" fw={500}>
                Begin Collaboration
              </Text>
              <Text fz="xs" c="slate.5">
                Share documents, complete compatibility questionnaires, and track progress
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Paper>

      <Text ta="center" fz="sm" c="slate.6">
        {bothPartiesPaid
          ? bothNdaFullySigned
            ? "Your NDA was accepted with payment. Open your workspace to get started."
            : "Open your collaboration workspace from Interests or Collaborations."
          : "Your NDA acceptance is saved. When the other party pays, your collaboration workspace will activate."}
      </Text>
    </Modal>
  );
}
